import axios from 'axios';
import dayjs from 'dayjs';
import { isNsMockEnabled, grantFull as grantNsMockFull } from '@/utils/next-steps-mock';
import {
  harnessCategories,
  harnessCondition,
  harnessMarkRedeemed,
  harnessVoucherById,
  harnessVouchers,
  isVoucherHarnessEnabled,
} from '@/utils/voucher-harness';
import { isExpired, isExpiringSoon } from '@/utils/voucher-expiry';

// DEV-ONLY: short-circuit into the Next Steps Mock Mode console's local state
// machine instead of hitting the API. See store/modules/courses.js for the
// matching hooks; gated on BOTH the mock toggle and non-production.
const nsMockActive = () => isNsMockEnabled() && process.env.VUE_APP_NODE_ENV !== 'production';

const VOUCHER_STATUS = {
  AVAILABLE: 'available',
  REDEEMED: 'redeemed',
  REVOKED: 'revoked',
  // A partner (IDR) collection pass whose order was cancelled. Deliberately not
  // folded into REVOKED: revoked vouchers are filtered out of the wallet
  // entirely, and a cancelled pass is documented as recoverable — hiding it
  // would make it vanish and then silently reappear.
  CANCELLED: 'cancelled',
  // A partner pass IDR has expired (they own expiry — end of day, Singapore
  // time). Its own status rather than a date, because the wallet row carries no
  // expiry of its own: a pure IDR pass has no curated voucher to take one from,
  // so this used to arrive as `available` and stayed on the "ready to use" pile
  // until the member was turned away at the counter.
  EXPIRED: 'expired',
};

/**
 * DEV-ONLY: the wallet harness serves fixtures instead of the API so every card
 * and detail branch is reachable without a backend. See utils/voucher-harness.js.
 * `loading` never resolves and `error` always throws, which is the only way to
 * see those two branches at all.
 */
const harnessHalt = async () => {
  const condition = harnessCondition();
  if (condition === 'loading') await new Promise(() => {});
  if (condition === 'error') throw new Error('Harness: forced load failure');
};

const isAvailable = voucher => voucher.status === VOUCHER_STATUS.AVAILABLE;
const isVisibleToMember = voucher => voucher.status !== VOUCHER_STATUS.REVOKED;
const sortByExpiryAsc = (a, b) => dayjs(a.expiresAt).valueOf() - dayjs(b.expiresAt).valueOf();
/** Needs attention: the deadline is close, or it has already passed. */
const needsAttention = voucher => isExpiringSoon(voucher) || isExpired(voucher);

const INITIAL_STATE = {
  vouchers: [],
  categories: [],
  selectedVoucher: null,
  isLoading: false,
  error: null,
  /**
   * Whether the wallet has been fetched at least once this session.
   *
   * An empty `vouchers` array cannot answer that on its own — a member with no
   * vouchers and a member who has not opened the wallet look identical — and
   * anything comparing before/after a fetch needs the difference. The purchase
   * flow uses it to decide whether it can honestly say the book arrived, or
   * only that it will.
   */
  hasLoaded: false,
};

const state = () => ({ ...INITIAL_STATE });

const mutations = {
  SET_VOUCHERS(state, vouchers) {
    state.vouchers = vouchers.filter(isVisibleToMember);
    state.hasLoaded = true;
  },
  SET_CATEGORIES(state, categories) {
    state.categories = categories;
  },
  SET_SELECTED_VOUCHER(state, voucher) {
    state.selectedVoucher = voucher && isVisibleToMember(voucher) ? voucher : null;
  },
  SET_LOADING(state, loading) {
    state.isLoading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  RESET_STATE(state) {
    Object.assign(state, INITIAL_STATE);
  },
};

const actions = {
  async fetchVouchers({ commit, dispatch, rootState }) {
    if (isVoucherHarnessEnabled()) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        await harnessHalt();
        const fixtures = harnessVouchers();
        commit('SET_VOUCHERS', fixtures);
        return fixtures;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    }
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      const accessToken = rootState.user.token;
      const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/v2/vouchers/my-vouchers`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      commit('SET_VOUCHERS', data);
      return data;
    } catch (error) {
      console.error('Error fetching vouchers:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load vouchers';
      commit('SET_ERROR', errorMessage);
      dispatch('toast/error', errorMessage, { root: true });
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchVoucherById({ commit, dispatch, rootState }, payload) {
    const voucherId = typeof payload === 'object' ? payload.id : payload;
    const isSilent = typeof payload === 'object' && payload.silent;

    if (isVoucherHarnessEnabled()) {
      if (!isSilent) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
      }
      try {
        await harnessHalt();
        // isVisibleToMember on purpose: a revoked fixture must resolve to null
        // here exactly as the API would, so the not-found branch is testable.
        const found = harnessVoucherById(voucherId);
        const visible = found && isVisibleToMember(found) ? found : null;
        commit('SET_SELECTED_VOUCHER', visible);
        return visible;
      } catch (error) {
        if (!isSilent) commit('SET_ERROR', error.message);
        throw error;
      } finally {
        if (!isSilent) commit('SET_LOADING', false);
      }
    }

    try {
      if (!isSilent) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
      }

      const accessToken = rootState.user.token;
      const { data } = await axios.get(
        `${process.env.VUE_APP_API_URL}/v2/vouchers/${encodeURIComponent(voucherId)}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );

      const visibleVoucher = isVisibleToMember(data) ? data : null;
      commit('SET_SELECTED_VOUCHER', visibleVoucher);
      return visibleVoucher;
    } catch (error) {
      console.error('Error fetching voucher:', error);
      if (!isSilent) {
        const errorMessage = error.response?.data?.message || 'Failed to load voucher details';
        commit('SET_ERROR', errorMessage);
        dispatch('toast/error', errorMessage, { root: true });
      }
      throw error;
    } finally {
      if (!isSilent) {
        commit('SET_LOADING', false);
      }
    }
  },

  /**
   * Asks whether this collection pass has been collected.
   *
   * IDR's inbound webhook is the primary writer; this button is the fallback
   * for the member standing at the counter before that hook is live, or if it
   * missed. The wallet list itself stays a local mirror.
   *
   * Driven by a press rather than a timer: polling on their behalf spends
   * battery guessing when they are at a counter. The server throttles per
   * order at 3s regardless of how often this is called.
   */
  async refreshClaimStatus({ commit, dispatch, state, rootState }, voucherId) {
    // Both offline modes, not just one. This action was added after the others
    // and only checked the Next Steps mock, so with the voucher harness on it
    // fired a live request at the dev API — from the very button the harness's
    // `collected` step tells you to press.
    if (isVoucherHarnessEnabled()) {
      const local = state.vouchers.find(v => v.id === voucherId) ?? null;
      commit('SET_SELECTED_VOUCHER', local);
      return local;
    }
    if (nsMockActive()) {
      // No partner to ask; the harness owns the state machine.
      return null;
    }
    const accessToken = rootState.user.token;
    const { data } = await axios.get(
      `${process.env.VUE_APP_API_URL}/v2/vouchers/${encodeURIComponent(voucherId)}/claim-status`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    const visible = isVisibleToMember(data) ? data : null;
    commit('SET_SELECTED_VOUCHER', visible);
    // A book that also unlocks a course grants on the server at collect time.
    // Refresh access here so lesson locks update without opening Courses.
    if (visible?.status === VOUCHER_STATUS.REDEEMED && visible.unlocksProgramSlug) {
      await dispatch('courses/fetchCourseAccess', null, { root: true });
    }
    return visible;
  },

  async fetchCategories({ commit, rootState }) {
    if (isVoucherHarnessEnabled()) {
      const categories = harnessCategories();
      commit('SET_CATEGORIES', categories);
      return categories;
    }
    try {
      commit('SET_ERROR', null);

      const accessToken = rootState.user.token;
      const { data } = await axios.get(`${process.env.VUE_APP_API_URL}/v2/vouchers/categories`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      commit('SET_CATEGORIES', data);
      return data;
    } catch (error) {
      console.error('Error fetching voucher categories:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load categories';
      commit('SET_ERROR', errorMessage);
      throw error;
    }
  },

  // In-app redemption for course-unlock vouchers: grants FULL course access on the
  // server and marks the voucher redeemed. On success we refresh the voucher list
  // (now redeemed) and — when a course was unlocked — the courses access summary so
  // lesson locks update immediately. Physical items are collected at the partner
  // counter instead, and the partner marks them collected on their side.
  async redeemVoucher({ commit, dispatch, rootState }, voucherId) {
    if (nsMockActive()) {
      grantNsMockFull('next-steps-p1', 'VOUCHER');
      await dispatch('courses/fetchCourseAccess', null, { root: true });
      return { status: 'redeemed', unlockedProgramSlug: 'next-steps-p1' };
    }
    if (isVoucherHarnessEnabled()) {
      await new Promise(resolve => setTimeout(resolve, 400));
      harnessMarkRedeemed(voucherId);
      const voucher = harnessVoucherById(voucherId);
      commit('SET_SELECTED_VOUCHER', voucher);
      return {
        id: voucherId,
        status: 'redeemed',
        redeemedAt: voucher?.redeemedAt ?? null,
        unlockedProgramSlug: voucher?.unlocksProgramSlug ?? null,
      };
    }
    try {
      commit('SET_ERROR', null);

      const accessToken = rootState.user.token;
      const { data } = await axios.post(
        `${process.env.VUE_APP_API_URL}/v2/vouchers/${voucherId}/redeem`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );

      await dispatch('fetchVouchers');
      if (data?.unlockedProgramSlug) {
        await dispatch('courses/fetchCourseAccess', null, { root: true });
      }
      return data;
    } catch (error) {
      console.error('Error redeeming voucher:', error);
      const errorMessage = error.response?.data?.message || 'Failed to redeem voucher';
      commit('SET_ERROR', errorMessage);
      dispatch('toast/error', errorMessage, { root: true });
      throw error;
    }
  },

  clearSelectedVoucher({ commit }) {
    commit('SET_SELECTED_VOUCHER', null);
  },

  resetState({ commit }) {
    commit('RESET_STATE');
  },
};

const getters = {
  // Usable, with no deadline worth worrying about — including a voucher that
  // does carry a far-off expiry date. Excluding those made Available an
  // incomplete answer to "what can I use".
  availableVouchers: state => {
    return state.vouchers.filter(v => isAvailable(v) && !needsAttention(v));
  },
  // Soonest first, so whatever is about to lapse is at the top. Already-expired
  // vouchers stay in this bucket rather than vanishing: they are still listed,
  // tagged Expired, with every action disabled.
  expiringVouchers: state => {
    return state.vouchers.filter(v => isAvailable(v) && needsAttention(v)).sort(sortByExpiryAsc);
  },
  redeemedVouchers: state => {
    return state.vouchers.filter(v => v.status === VOUCHER_STATUS.REDEEMED);
  },
  // A cancelled partner order. It had no tab at all until now, so a member's
  // book simply vanished from the wallet — and would reappear unexplained if
  // IDR restored the order, which is the exact failure keeping `cancelled`
  // distinct from `revoked` was meant to prevent.
  unavailableVouchers: state => {
    return state.vouchers.filter(v => v.status === VOUCHER_STATUS.CANCELLED);
  },
  // Kept out of `unavailable`: a cancelled order can be restored, an expired
  // pass cannot. It belongs with history, not with "something went wrong".
  expiredVouchers: state => {
    return state.vouchers.filter(v => v.status === VOUCHER_STATUS.EXPIRED);
  },
  // No voucherStatusCounts / isVoucherExpired here. Both were kept alive only by
  // their own tests: the wallet counts its own buckets in vouchers/index.vue (and
  // computed a DIFFERENT answer to the getter's, which is the reason not to have
  // two), and the detail page calls the shared isExpired helper directly.
  isLoading: state => state.isLoading,
  error: state => state.error,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
